import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Search, Pencil, Archive, Eye, Users as UsersIcon, RefreshCw,
  CheckCircle2, SlidersHorizontal, ArrowUpDown, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StatusBadge } from '../components/common/StatusBadge';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyState } from '../components/common/EmptyState';
import { EmployeeModal } from '../components/employees/EmployeeModal';
import { employeeService } from '../services/employee.service';
import apiClient from '../services/apiClient';
import { KEYS } from '../lib/queryKeys';
import { formatINR } from '../lib/format';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Employee Profile Modal ─────────────────────────────────────────────────────
function EmployeeProfileModal({ employeeId, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());

  const { data, isLoading } = useQuery({
    queryKey: ['employeeProfile', employeeId, year],
    queryFn:  () => apiClient.get(`/api/employees/${employeeId}/profile`, { params: { year } }).then(r => r.data),
    enabled:  !!employeeId,
  });

  const profile = data?.data;
  const emp     = profile?.employee;
  const history = profile?.payrollHistory || [];
  const ytd     = profile?.ytd || {};
  const lb      = profile?.leaveBalance;
  const att     = profile?.attendanceSummary || {};

  return (
    <Dialog open={!!employeeId} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          {emp && (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-lg font-semibold flex-shrink-0">
                {emp.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-lg font-semibold text-slate-900">{emp.name}</DialogTitle>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  <span className="text-xs text-slate-500 font-mono">{emp.employeeCode}</span>
                  <span className="text-xs text-slate-300">·</span>
                  <span className="text-xs text-slate-500">{emp.department}</span>
                  <span className="text-xs text-slate-300">·</span>
                  <span className="text-xs text-slate-500 capitalize">{emp.employmentType?.replace('-',' ')}</span>
                  <StatusBadge status={emp.status === 'active' ? 'Active' : 'Archived'} />
                </div>
              </div>
              <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
                <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{[2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </DialogHeader>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 flex-1">Loading profile…</div>
        ) : profile ? (
          <div className="flex-1 overflow-y-auto">
            <Tabs defaultValue="overview">
              <TabsList className="mx-6 mt-4 bg-white border border-slate-200 p-1 h-auto gap-1">
                {['overview','payroll','leave','salary'].map(t => (
                  <TabsTrigger key={t} value={t}
                    className="data-[state=active]:bg-[#2E6DA4] data-[state=active]:text-white px-3 py-1.5 text-xs capitalize">
                    {t === 'payroll' ? 'Payroll History' : t === 'leave' ? 'Leave & Attendance' : t === 'salary' ? 'Salary Structure' : 'Overview'}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Overview */}
              <TabsContent value="overview" className="px-6 py-4 space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">YTD Summary {year}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      { label: 'YTD Gross',     value: formatINR(ytd.grossEarnings), color: 'text-slate-900',   bg: 'bg-white' },
                      { label: 'YTD Net Pay',   value: formatINR(ytd.netPay),        color: 'text-[#2E6DA4]',  bg: 'bg-blue-50' },
                      { label: 'YTD PF',        value: formatINR(ytd.pfDeduction),   color: 'text-red-600',    bg: 'bg-red-50' },
                      { label: 'YTD ESI',       value: formatINR(ytd.esiDeduction),  color: 'text-red-600',    bg: 'bg-red-50' },
                      { label: 'YTD LOP',       value: formatINR(ytd.lopDeduction),  color: 'text-amber-600',  bg: 'bg-amber-50' },
                      { label: 'YTD Bonus',     value: formatINR(ytd.bonus),         color: 'text-emerald-600',bg: 'bg-emerald-50' },
                    ].map(c => (
                      <div key={c.label} className={`p-3 rounded-lg border border-slate-200 ${c.bg}`}>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
                        <div className={`text-lg font-bold font-mono mt-1 ${c.color}`}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Attendance {year}</div>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { label: 'Present',    value: att.presentDays,   color: 'text-emerald-600', bg: 'bg-emerald-50' },
                      { label: 'Absent',     value: att.absentDays,    color: 'text-red-600',     bg: 'bg-red-50' },
                      { label: 'Half Days',  value: att.halfDays,      color: 'text-amber-600',   bg: 'bg-amber-50' },
                      { label: 'Paid Leave', value: att.paidLeaveDays, color: 'text-blue-600',    bg: 'bg-blue-50' },
                      { label: 'LOP Days',   value: att.lopDays,       color: 'text-red-700',     bg: 'bg-red-50' },
                    ].map(c => (
                      <div key={c.label} className={`p-3 rounded-lg border border-slate-200 text-center ${c.bg}`}>
                        <div className={`text-2xl font-bold font-mono ${c.color}`}>{c.value || 0}</div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{c.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Paid months in {year}</div>
                  <div className="flex flex-wrap gap-2">
                    {history.filter(h => h.year === year).length === 0
                      ? <span className="text-xs text-slate-400">No payroll runs yet for {year}</span>
                      : history.filter(h => h.year === year).map((h, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span className="text-xs font-medium text-emerald-700">{MONTHS[h.month-1]} — {formatINR(h.netPay)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </TabsContent>

              {/* Payroll History */}
              <TabsContent value="payroll" className="px-6 py-4">
                {history.length === 0 ? (
                  <div className="py-10 text-center text-slate-400 text-sm">No payroll history for {year}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          {['Month','Days','Gross','PF','ESI','LOP','Bonus','Net Pay'].map(h => (
                            <th key={h} className="px-3 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider text-right first:text-left">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {history.map((h, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-3 py-2.5 font-medium text-slate-900">{MONTHS[h.month-1]} {h.year}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs">
                              <span>{h.daysPresent}P</span>
                              {h.halfDays > 0 && <span className="text-amber-600 ml-1">{h.halfDays}H</span>}
                              {h.lopDays > 0  && <span className="text-red-600 ml-1">{h.lopDays}L</span>}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs">{formatINR(h.grossEarnings)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs text-red-600">{h.pfDeduction  ? `-${formatINR(h.pfDeduction)}`  : '—'}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs text-red-600">{h.esiDeduction ? `-${formatINR(h.esiDeduction)}` : '—'}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs text-red-600">{h.lopDeduction ? `-${formatINR(h.lopDeduction)}` : '—'}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs text-emerald-600">{h.bonus ? formatINR(h.bonus) : '—'}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-xs font-semibold text-[#2E6DA4]">{formatINR(h.netPay)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                        <tr className="font-mono text-xs">
                          <td className="px-3 py-2.5 font-sans font-semibold text-slate-900" colSpan={2}>YTD Total</td>
                          <td className="px-3 py-2.5 text-right font-semibold">{formatINR(ytd.grossEarnings)}</td>
                          <td className="px-3 py-2.5 text-right text-red-600">-{formatINR(ytd.pfDeduction)}</td>
                          <td className="px-3 py-2.5 text-right text-red-600">-{formatINR(ytd.esiDeduction)}</td>
                          <td className="px-3 py-2.5 text-right text-red-600">-{formatINR(ytd.lopDeduction)}</td>
                          <td className="px-3 py-2.5 text-right text-emerald-600">{formatINR(ytd.bonus)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-[#2E6DA4]">{formatINR(ytd.netPay)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </TabsContent>

              {/* Leave & Attendance */}
              <TabsContent value="leave" className="px-6 py-4 space-y-4">
                {lb ? (
                  <>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Leave Balance {year}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {['PL','CL','SL'].map(t => (
                        <div key={t} className="p-3 rounded-lg border border-slate-200 bg-white">
                          <div className="text-xs font-semibold text-slate-600 mb-2">
                            {t === 'PL' ? 'Privilege' : t === 'CL' ? 'Casual' : 'Sick'} Leave
                          </div>
                          <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Due</span><span className="font-mono">{lb[t]?.entitled ?? 0}</span></div>
                          <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Used</span><span className="font-mono text-amber-600">{lb[t]?.used ?? 0}</span></div>
                          <div className="flex justify-between text-sm font-semibold"><span>Left</span>
                            <span className={`font-mono ${(lb[t]?.remaining??0)===0?'text-red-600':(lb[t]?.remaining??0)<=2?'text-amber-600':'text-emerald-600'}`}>
                              {lb[t]?.remaining ?? 0}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="p-3 rounded-lg border border-red-200 bg-red-50">
                        <div className="text-xs font-semibold text-red-700 mb-2">LOP Days</div>
                        <div className="text-2xl font-bold font-mono text-red-600 mt-3">{lb.LOP?.used ?? 0}</div>
                        <div className="text-[10px] text-red-500 mt-1">Loss of Pay</div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-6 text-center text-slate-400 text-sm">No leave data for {year}</div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Deduction Rates</div>
                  <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-2">
                    {[
                      ['Daily rate (Gross ÷ 26)',    formatINR((emp?.salaryStructure?.grossFixed||0)/26) + '/day'],
                      ['Half-day deduction',          formatINR((emp?.salaryStructure?.grossFixed||0)/26/2) + '/half-day'],
                      ['LOP deduction (full day)',    formatINR((emp?.salaryStructure?.grossFixed||0)/26) + '/day'],
                    ].map(([l,v]) => (
                      <div key={l} className="flex justify-between">
                        <span>{l}</span>
                        <span className="font-mono font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Salary Structure */}
              <TabsContent value="salary" className="px-6 py-4 space-y-4">
                {emp && (
                  <>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Salary Breakdown</div>
                      <div className="space-y-2">
                        {[
                          { label: 'Basic Salary', value: emp.salaryStructure?.basic || 0 },
                          { label: 'HRA',          value: emp.salaryStructure?.hra   || 0 },
                          { label: 'Allowances',   value: emp.salaryStructure?.allowances || 0 },
                          ...(emp.salaryStructure?.customAllowances||[]).map(a => ({ label: a.label, value: a.amount })),
                        ].map((item, i) => (
                          <div key={i} className="flex justify-between items-center p-2.5 rounded-md bg-slate-50 border border-slate-200">
                            <span className="text-sm text-slate-700">{item.label}</span>
                            <span className="text-sm font-mono font-semibold text-slate-900">{formatINR(item.value)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between items-center p-2.5 rounded-md bg-[#2E6DA4]/5 border border-[#2E6DA4]/20">
                          <span className="text-sm font-semibold text-[#2E6DA4]">Total Gross</span>
                          <span className="text-sm font-mono font-bold text-[#2E6DA4]">{formatINR(emp.salaryStructure?.grossFixed||0)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Statutory Compliance</div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'PF (12%)', enabled: emp.pfApplicable,  sub: emp.pfUAN ? `UAN: ${emp.pfUAN}` : '12% of basic' },
                          { label: 'ESI (0.75%)', enabled: emp.esiApplicable, sub: emp.esiNumber ? `IP: ${emp.esiNumber}` : '0.75% of gross' },
                          { label: 'PT', enabled: emp.ptApplicable, sub: 'State slab' },
                        ].map(item => (
                          <div key={item.label} className={`p-3 rounded-lg border text-center ${item.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                            <div className={`text-sm font-semibold ${item.enabled ? 'text-emerald-700' : 'text-slate-400'}`}>{item.label}</div>
                            <div className={`text-xs mt-1 ${item.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>{item.enabled ? 'Applicable' : 'Not applicable'}</div>
                            {item.enabled && <div className="text-[10px] text-slate-500 mt-1 font-mono">{item.sub}</div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {emp.bankDetails?.accountNumber && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Bank Details</div>
                        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-1.5 text-sm">
                          {[
                            ['Bank',    emp.bankDetails.bankName||'—'],
                            ['Account', emp.bankDetails.accountNumber],
                            ['IFSC',    emp.bankDetails.ifscCode||'—'],
                            ['Type',    emp.bankDetails.accountType],
                          ].map(([l,v]) => (
                            <div key={l} className="flex justify-between">
                              <span className="text-slate-500">{l}</span>
                              <span className="font-mono capitalize">{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ── Employee List Page ─────────────────────────────────────────────────────────
export default function Employees() {
  const qc = useQueryClient();
  const [search,     setSearch]    = useState('');
  const [status,     setStatus]    = useState('active');
  const [deptFilter, setDeptFilter]= useState('all');
  const [typeFilter, setTypeFilter]= useState('all');
  const [sortBy,     setSortBy]    = useState('name_asc');
  const [modalOpen,  setModal]     = useState(false);
  const [editEmp,    setEditEmp]   = useState(null);
  const [profileId,  setProfileId] = useState(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: KEYS.employees({ status }),
    queryFn:  () => employeeService.list({ status }),
    staleTime: 30_000,
  });
  const allEmployees = data?.data || [];

  // Derive departments from data
  const departments = useMemo(() =>
    [...new Set(allEmployees.map(e => e.department).filter(Boolean))].sort(),
  [allEmployees]);

  // Client-side filter + sort
  const employees = useMemo(() => {
    let list = allEmployees.filter(emp => {
      if (deptFilter !== 'all' && emp.department !== deptFilter) return false;
      if (typeFilter !== 'all' && emp.employmentType !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return emp.name.toLowerCase().includes(q) ||
               (emp.email||'').toLowerCase().includes(q) ||
               (emp.phone||'').includes(q) ||
               (emp.department||'').toLowerCase().includes(q) ||
               (emp.employeeCode||'').toLowerCase().includes(q);
      }
      return true;
    });

    list = list.slice().sort((a, b) => {
      const gA = a.salaryStructure?.grossFixed || 0;
      const gB = b.salaryStructure?.grossFixed || 0;
      switch(sortBy) {
        case 'name_desc':  return b.name.localeCompare(a.name);
        case 'gross_desc': return gB - gA;
        case 'gross_asc':  return gA - gB;
        case 'joined_desc':return new Date(b.joinedAt) - new Date(a.joinedAt);
        case 'joined_asc': return new Date(a.joinedAt) - new Date(b.joinedAt);
        default:           return a.name.localeCompare(b.name); // name_asc
      }
    });

    return list;
  }, [allEmployees, deptFilter, typeFilter, search, sortBy]);

  const qc2 = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['employees'] });

  const createMut = useMutation({
    mutationFn: (d) => employeeService.create(d),
    onSuccess: (r) => { toast.success('Employee added', { description: r.data.name }); invalidate(); setModal(false); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to add'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...d }) => employeeService.update(id, d),
    onSuccess: (r) => { toast.success('Employee updated', { description: r.data.name }); invalidate(); setModal(false); setEditEmp(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const archiveMut = useMutation({
    mutationFn: (id) => employeeService.archive(id),
    onSuccess: () => { toast.success('Employee archived'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const restoreMut = useMutation({
    mutationFn: (id) => employeeService.restore(id),
    onSuccess: () => { toast.success('Employee restored'); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const handleSave = (formData) => {
    const payload = {
      name: formData.name, phone: formData.phone, email: formData.email,
      department: formData.department, designation: formData.designation,
      employmentType: formData.type?.toLowerCase().replace(' ', '-') || 'full-time',
      joinedAt: formData.joined,
      salaryStructure: {
        basic: Number(formData.basic), hra: Number(formData.hra),
        customAllowances: (formData.customAllowances||[]).filter(a => a.label.trim() !== '' || Number(a.amount) > 0),
      },
      pfApplicable:  !!formData.pf,
      esiApplicable: !!formData.esi,
      ptApplicable:  !!formData.pt,
    };
    if (editEmp) updateMut.mutate({ id: editEmp._id, ...payload });
    else createMut.mutate(payload);
  };

  const mapToForm = (emp) => {
    if (!emp) return null;
    const s = emp.salaryStructure || {};
    return {
      name: emp.name, phone: emp.phone, email: emp.email,
      type: emp.employmentType, department: emp.department, designation: emp.designation,
      basic: s.basic, hra: s.hra, customAllowances: s.customAllowances || [],
      pf: emp.pfApplicable, esi: emp.esiApplicable, pt: emp.ptApplicable,
      joined: emp.joinedAt?.slice(0, 10),
    };
  };

  const hasFilters = deptFilter !== 'all' || typeFilter !== 'all' || search;
  const totalGross  = employees.reduce((s, e) => s + (e.salaryStructure?.grossFixed||0), 0);
  const pfCount     = employees.filter(e => e.pfApplicable).length;
  const esiCount    = employees.filter(e => e.esiApplicable).length;

  return (
    <div>
      <PageHeader
        title="Employees"
        description={`${allEmployees.filter(e=>e.status==='active').length} active · ${allEmployees.filter(e=>e.status==='archived').length} archived`}
      >
        <Button onClick={() => { setEditEmp(null); setModal(true); }} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Add Employee
        </Button>
      </PageHeader>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        {[
          { label: 'Total Active',     value: allEmployees.filter(e=>e.status==='active').length, sub: `${departments.length} dept${departments.length!==1?'s':''}`, color: 'text-slate-900' },
          { label: 'Total Gross / Mo', value: formatINR(allEmployees.filter(e=>e.status==='active').reduce((s,e)=>s+(e.salaryStructure?.grossFixed||0),0)), sub: 'all active employees', color: 'text-[#2E6DA4]' },
          { label: 'PF Enrolled',      value: `${pfCount}/${employees.length}`,  sub: employees.length>0?`${Math.round(pfCount/employees.length*100)}% coverage`:'—', color: 'text-emerald-600' },
          { label: 'ESI Enrolled',     value: `${esiCount}/${employees.length}`, sub: employees.length>0?`${Math.round(esiCount/employees.length*100)}% coverage`:'—', color: 'text-blue-600' },
        ].map(c => (
          <Card key={c.label} className="p-4 border-slate-200 shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
            <div className={`text-xl font-bold font-mono mt-1.5 ${c.color}`}>{c.value}</div>
            <div className="text-xs text-slate-400 mt-0.5">{c.sub}</div>
          </Card>
        ))}
      </div>

      {/* Filter bar */}
      <Card className="p-4 mb-5 border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          {/* Search */}
          <div className="flex-1 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Name, email, phone, dept, code…" className="pl-8 h-9" />
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1 min-w-[120px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Department */}
          <div className="space-y-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Department</Label>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Employment type */}
          <div className="space-y-1 min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="full-time">Full-time</SelectItem>
                <SelectItem value="part-time">Part-time</SelectItem>
                <SelectItem value="daily-wage">Daily wage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sort */}
          <div className="space-y-1 min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Sort by</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name A → Z</SelectItem>
                <SelectItem value="name_desc">Name Z → A</SelectItem>
                <SelectItem value="gross_desc">Salary — highest</SelectItem>
                <SelectItem value="gross_asc">Salary — lowest</SelectItem>
                <SelectItem value="joined_desc">Joined — newest</SelectItem>
                <SelectItem value="joined_asc">Joined — oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reset */}
          {hasFilters && (
            <Button variant="outline" size="sm" className="h-9 shrink-0"
              onClick={() => { setSearch(''); setDeptFilter('all'); setTypeFilter('all'); setSortBy('name_asc'); }}>
              Reset
            </Button>
          )}

          {isFetching && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin shrink-0" />}
        </div>

        {/* Filter summary */}
        {employees.length > 0 && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
            <span>{employees.length} employee{employees.length!==1?'s':''}</span>
            <span className="text-slate-300">|</span>
            <span>Total gross: <span className="font-mono font-semibold text-[#2E6DA4]">{formatINR(totalGross)}/mo</span></span>
            {pfCount > 0  && <><span className="text-slate-300">|</span><span className="text-emerald-600">{pfCount} PF</span></>}
            {esiCount > 0 && <><span className="text-slate-300">|</span><span className="text-blue-600">{esiCount} ESI</span></>}
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900 font-heading">Employee List</h3>
          <span className="text-xs text-slate-400">{employees.length} of {allEmployees.length}</span>
        </div>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading employees…</div>
        ) : employees.length === 0 ? (
          <EmptyState icon={UsersIcon}
            title={allEmployees.length === 0 ? 'No employees found' : 'No employees match filters'}
            description={allEmployees.length === 0 ? 'Add your first employee to get started.' : 'Try changing the search or filters.'}
            actionLabel={allEmployees.length === 0 ? 'Add Employee' : undefined}
            onAction={allEmployees.length === 0 ? () => { setEditEmp(null); setModal(true); } : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Employee','Code','Department','Type','Joined','Gross Salary','Compliance','Actions'].map(h => (
                    <th key={h} className={`px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider ${
                      h==='Gross Salary'||h==='Actions' ? 'text-right' : 'text-left'
                    }`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {employees.map(emp => {
                  const s = emp.salaryStructure || {};
                  const gross = s.grossFixed || (s.basic||0)+(s.hra||0)+(s.allowances||0);
                  return (
                    <tr key={emp._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {emp.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900">{emp.name}</div>
                            <div className="text-xs text-slate-400">{emp.email || emp.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-xs font-mono text-slate-500">{emp.employeeCode||'—'}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{emp.department||'—'}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-600 capitalize">{emp.employmentType?.replace('-',' ')}</td>
                      <td className="px-5 py-3.5 text-xs text-slate-500">
                        {emp.joinedAt ? new Date(emp.joinedAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-right font-mono font-semibold text-slate-900">{formatINR(gross)}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex gap-1">
                          {emp.pfApplicable  && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">PF</span>}
                          {emp.esiApplicable && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">ESI</span>}
                          {emp.ptApplicable  && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold">PT</span>}
                          {!emp.pfApplicable && !emp.esiApplicable && !emp.ptApplicable && <span className="text-[10px] text-slate-300">—</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setProfileId(emp._id)} className="h-8 px-2 text-slate-600 hover:text-[#2E6DA4]">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setEditEmp(emp); setModal(true); }} className="h-8 px-2 text-slate-600 hover:text-[#2E6DA4]">
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm"
                            onClick={() => emp.status==='active' ? archiveMut.mutate(emp._id) : restoreMut.mutate(emp._id)}
                            className="h-8 px-2 text-slate-600 hover:text-red-600">
                            {emp.status==='active' ? <Archive className="w-3.5 h-3.5" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <EmployeeModal
        open={modalOpen}
        onOpenChange={o => { setModal(o); if (!o) setEditEmp(null); }}
        onSave={handleSave}
        employee={mapToForm(editEmp)}
      />

      {profileId && <EmployeeProfileModal employeeId={profileId} onClose={() => setProfileId(null)} />}
    </div>
  );
}